import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle,
  CreditCard,
  RefreshCcw,
  Shield,
  X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { isLocalPreviewMode } from '../lib/localPreview';
import useSEO from '../hooks/useSEO';
import { getPlanDisplayName } from '../utils/subscriptionAccess';

const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes
const EDGE_TIMEOUT_MS = 10000; // 10 seconds

const formatDate = (value) => {
  if (!value) return '';
  const date = typeof value === 'number' ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const formatMoney = (amountInMinorUnits, currency = 'GBP') => {
  if (typeof amountInMinorUnits !== 'number') return null;
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: String(currency || 'GBP').toUpperCase(),
    }).format(amountInMinorUnits / 100);
  } catch (error) {
    return null;
  }
};

const getIntervalSuffix = (interval) => {
  switch (interval) {
    case 'year':
      return '/year';
    case 'week':
      return '/week';
    case 'day':
      return '/day';
    default:
      return '/month';
  }
};

const normalizeMessage = (value, fallback) => {
  if (value instanceof Error) return value.message || fallback;
  if (typeof value === 'string') return value || fallback;
  return fallback;
};

const collectDiscountCandidates = (subscription) => {
  const candidates = [];
  if (!subscription) return candidates;

  if (subscription.discount) {
    candidates.push(subscription.discount);
  }

  const discounts = subscription.discounts;
  if (discounts) {
    if (Array.isArray(discounts)) {
      candidates.push(...discounts);
    } else if (Array.isArray(discounts.data)) {
      candidates.push(...discounts.data);
    }
  }

  return candidates;
};

const extractDiscountSummary = (subscription) => {
  if (!subscription?.items?.data?.length) {
    return null;
  }

  const lineItem = subscription.items.data[0];
  const price = lineItem.price || {};
  const unitAmount = typeof price.unit_amount === 'number' ? price.unit_amount : null;

  if (unitAmount === null) {
    return null;
  }

  const currency = (price.currency || 'GBP').toUpperCase();
  const interval = price.recurring?.interval || 'month';
  const nextBillDate = subscription.current_period_end ? subscription.current_period_end * 1000 : null;

  const candidates = collectDiscountCandidates(subscription);
  const discount = candidates.find((entry) => {
    const coupon = entry?.coupon;
    if (!coupon) return false;
    if (coupon.duration && coupon.duration !== 'once') return false;
    return typeof coupon.percent_off === 'number' || typeof coupon.amount_off === 'number';
  });

  if (!discount) {
    return null;
  }

  const coupon = discount.coupon || {};
  const percentOff = typeof coupon.percent_off === 'number' ? coupon.percent_off : null;
  const amountOff = typeof coupon.amount_off === 'number' ? coupon.amount_off : null;

  let discountedAmount = null;
  if (percentOff !== null) {
    discountedAmount = Math.max(0, Math.round(unitAmount * (1 - percentOff / 100)));
  } else if (amountOff !== null) {
    discountedAmount = Math.max(0, unitAmount - amountOff);
  }

  return {
    percentOff,
    amountOff,
    baseAmount: unitAmount,
    discountedAmount,
    currency,
    interval,
    nextBillDate,
    couponName: coupon.name || null
  };
};

const ModalShell = ({ title, tone = 'default', children, onClose }) => {
  const toneStyles = tone === 'danger'
    ? 'border-red-100'
    : tone === 'success'
      ? 'border-teal-100'
      : 'border-slate-100';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm px-4">
      <div className={`w-full max-w-lg rounded-3xl bg-white border p-6 shadow-xl ${toneStyles} overflow-hidden`}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
          <h2 className="text-xl font-bold text-slate-900 tracking-tight">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-6">{children}</div>
      </div>
    </div>
  );
};

const ManageSubscription = () => {
  useSEO({
    title: 'Manage subscription | APDoneRight',
    description: 'Manage your APDoneRight subscription and billing details.',
    canonical: '/manage-subscription',
    robots: 'noindex,follow',
  });

  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuth();
  const localPreviewMode = isLocalPreviewMode();

  const lastFetchAtRef = useRef(0);
  const queryHandledRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [edgeError, setEdgeError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [subscription, setSubscription] = useState(null);
  const [eligibleForDownsell, setEligibleForDownsell] = useState(false);
  const [downsellRedeemedAt, setDownsellRedeemedAt] = useState(null);
  const [activeAction, setActiveAction] = useState(null);
  const [showDownsellOffer, setShowDownsellOffer] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const subscriptionType = String(profile?.subscription_type || 'free').toLowerCase();
  const planName = useMemo(() => getPlanDisplayName(profile), [profile]);
  const previewSubscription = useMemo(() => ({
    id: 'local-preview-subscription',
    status: 'active',
    cancel_at_period_end: false,
    current_period_end: Math.floor(Date.now() / 1000) + (30 * 24 * 60 * 60),
    items: {
      data: [
        {
          price: {
            unit_amount: 1900,
            currency: 'gbp',
            recurring: {
              interval: 'month'
            }
          }
        }
      ]
    }
  }), []);

  const invokeEdgeFunction = useCallback(async (functionName, body = {}, { timeoutMs = EDGE_TIMEOUT_MS } = {}) => {
    if (localPreviewMode) {
      switch (functionName) {
        case 'get-subscription-details':
          return {
            subscription: previewSubscription,
            eligibleForDownsell: true,
            downsellRedeemedAt: null
          };
        case 'create-setup-intent':
          return {
            localPreview: true
          };
        case 'cancel-subscription':
          return {
            message: 'Cancellation simulated in test mode.'
          };
        case 'apply-downsell':
          return {
            message: 'Offer applied for the next renewal in test mode.'
          };
        default:
          return {};
      }
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const { data, error } = await supabase.functions.invoke(functionName, {
        body,
        signal: controller.signal
      });

      if (error) {
        throw new Error(error.message || 'Request failed.');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      return data;
    } catch (err) {
      if (err?.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }
  }, [localPreviewMode, previewSubscription]);

  const fetchSubscriptionDetails = useCallback(async ({ forceRefresh = false } = {}) => {
    if (!user) return;

    const now = Date.now();
    if (!forceRefresh && now - lastFetchAtRef.current < CACHE_DURATION_MS) {
      return;
    }

    setEdgeError('');
    setLoading(true);

    try {
      if (!profile?.stripe_customer_id) {
        setSubscription(null);
        setEligibleForDownsell(false);
        setDownsellRedeemedAt(null);
        setEdgeError('No billing account found yet. Choose a plan to get started.');
        return;
      }

      const data = await invokeEdgeFunction('get-subscription-details', {}, { timeoutMs: EDGE_TIMEOUT_MS });
      setSubscription(data?.subscription || null);
      setEligibleForDownsell(Boolean(data?.eligibleForDownsell));
      setDownsellRedeemedAt(data?.downsellRedeemedAt || null);
      lastFetchAtRef.current = Date.now();
    } catch (err) {
      setEdgeError(normalizeMessage(err, 'Failed to load subscription details.'));
    } finally {
      setLoading(false);
    }
  }, [invokeEdgeFunction, profile?.stripe_customer_id, user]);

  useEffect(() => {
    if (!user) {
      navigate('/signin', { replace: true });
      return;
    }

    fetchSubscriptionDetails();
  }, [fetchSubscriptionDetails, navigate, user]);

  useEffect(() => {
    if (!user || queryHandledRef.current) return;
    queryHandledRef.current = true;

    const params = new URLSearchParams(location.search);
    const setupSuccess = params.get('setup_success') === 'true';
    const setupCanceled = params.get('setup_canceled') === 'true';
    const paymentUpdated = params.get('payment_updated') === '1';

    if (setupSuccess || paymentUpdated) {
      setSuccessMessage('Payment method updated.');
      fetchSubscriptionDetails({ forceRefresh: true });
    }

    if (setupCanceled) {
      setEdgeError('Payment update canceled.');
    }

    if (setupSuccess || setupCanceled || paymentUpdated) {
      navigate('/manage-subscription', { replace: true });
    }
  }, [fetchSubscriptionDetails, location.search, navigate, user]);

  const discountSummary = useMemo(
    () => extractDiscountSummary(subscription),
    [subscription]
  );

  const subscriptionStatus = subscription?.status ? String(subscription.status) : null;
  const subscriptionPrice = subscription?.items?.data?.[0]?.price || null;
  const baseUnitAmount = typeof subscriptionPrice?.unit_amount === 'number' ? subscriptionPrice.unit_amount : null;
  const currency = subscriptionPrice?.currency ? subscriptionPrice.currency.toUpperCase() : 'GBP';
  const interval = subscriptionPrice?.recurring?.interval || 'month';
  const intervalSuffix = getIntervalSuffix(interval);
  const nextBillDate = subscription?.current_period_end ? subscription.current_period_end * 1000 : null;
  const cancelAtPeriodEnd = Boolean(subscription?.cancel_at_period_end);
  const cancelDate = cancelAtPeriodEnd && nextBillDate ? formatDate(nextBillDate) : '';

  const hasActiveSubscription = Boolean(subscription && (subscriptionStatus === 'active' || subscriptionStatus === 'trialing'));

  const handleRefresh = async () => {
    setSuccessMessage('');
    setActiveAction('refresh');
    await fetchSubscriptionDetails({ forceRefresh: true });
    setActiveAction(null);
  };

  const handleUpdatePaymentMethod = async () => {
    setEdgeError('');
    setSuccessMessage('');
    setActiveAction('payment');

    try {
      if (localPreviewMode) {
        navigate('/manage-subscription/update-card', {
          state: {
            localPreview: true
          }
        });
        return;
      }

      const data = await invokeEdgeFunction('create-setup-intent', {}, { timeoutMs: EDGE_TIMEOUT_MS });

      if (data?.setupUrl) {
        window.location.href = data.setupUrl;
        return;
      }

      if (data?.clientSecret) {
        navigate('/manage-subscription/update-card', {
          state: {
            clientSecret: data.clientSecret,
            setupIntentId: data.setupIntentId ?? null,
            setupUrl: data.setupUrl || ''
          }
        });
        return;
      }

      throw new Error('Payment update link unavailable.');
    } catch (err) {
      setEdgeError(normalizeMessage(err, 'Failed to open payment method update.'));
    } finally {
      setActiveAction(null);
    }
  };

  const handleRequestCancel = () => {
    if (!hasActiveSubscription || cancelAtPeriodEnd) return;
    setEdgeError('');
    setSuccessMessage('');

    if (eligibleForDownsell && !downsellRedeemedAt) {
      setShowDownsellOffer(true);
      return;
    }

    setShowCancelConfirm(true);
  };

  const confirmCancel = async () => {
    setActiveAction('cancel');
    setEdgeError('');
    setSuccessMessage('');

    try {
      const body = subscription?.id ? { subscriptionId: subscription.id } : {};
      const data = await invokeEdgeFunction('cancel-subscription', body, { timeoutMs: EDGE_TIMEOUT_MS });
      setSuccessMessage(data?.message || 'Subscription scheduled for cancellation.');
      setShowCancelConfirm(false);
      if (localPreviewMode) {
        setSubscription((current) => current ? { ...current, cancel_at_period_end: true } : current);
        return;
      }
      await fetchSubscriptionDetails({ forceRefresh: true });
    } catch (err) {
      setEdgeError(normalizeMessage(err, 'Failed to cancel subscription.'));
    } finally {
      setActiveAction(null);
    }
  };

  const applyDownsell = async () => {
    setActiveAction('downsell');
    setEdgeError('');
    setSuccessMessage('');

    try {
      const data = await invokeEdgeFunction('apply-downsell', {}, { timeoutMs: EDGE_TIMEOUT_MS });
      setSuccessMessage(data?.message || '50% off applied to your next renewal.');
      setShowDownsellOffer(false);
      setEligibleForDownsell(false);
      setDownsellRedeemedAt(new Date().toISOString());
      if (localPreviewMode) {
        return;
      }
      await fetchSubscriptionDetails({ forceRefresh: true });
    } catch (err) {
      setEdgeError(normalizeMessage(err, 'Failed to apply offer.'));
    } finally {
      setActiveAction(null);
    }
  };

  const discountedAmount = typeof discountSummary?.discountedAmount === 'number'
    ? discountSummary.discountedAmount
    : null;
  const discountedPriceText = discountedAmount !== null
    ? formatMoney(discountedAmount, discountSummary?.currency || currency)
    : null;
  const basePriceText = baseUnitAmount !== null ? formatMoney(baseUnitAmount, currency) : null;

  return (
    <>
      <div className="ap-billing-page relative min-h-screen overflow-hidden text-slate-900">
        {/* Soft Background Orbs */}
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-teal-100 rounded-full mix-blend-multiply filter blur-[100px] opacity-70 animate-blob pointer-events-none"></div>
        <div className="absolute top-[20%] right-[-10%] w-[40%] h-[40%] bg-cyan-100 rounded-full mix-blend-multiply filter blur-[100px] opacity-70 animate-blob animation-delay-2000 pointer-events-none"></div>

        <div className="relative container mx-auto px-4 py-12 max-w-6xl">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 border border-teal-100 bg-teal-50 rounded-full mb-4">
                <Shield className="w-4 h-4 text-teal-600" />
                <span className="text-sm font-semibold text-teal-800 tracking-wide">Billing & Plan</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">
                Manage Subscription
              </h1>
              <p className="mt-4 text-slate-500 text-base font-medium max-w-2xl leading-relaxed">
                Update your card, check billing dates, and manage your plan.
              </p>
            </div>

            <button
              type="button"
              onClick={handleRefresh}
              disabled={activeAction === 'refresh' || loading}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-slate-200 bg-white text-slate-700 font-semibold hover:bg-slate-50 transition-colors disabled:opacity-60 rounded-full shadow-sm"
            >
              <RefreshCcw className={`w-4 h-4 text-slate-400 ${activeAction === 'refresh' || loading ? 'animate-spin text-teal-500' : ''}`} />
              Refresh
            </button>
          </div>

          {successMessage ? (
            <div className="mt-8 border border-teal-100 bg-teal-50 px-5 py-4 flex items-start gap-3 text-teal-800 rounded-2xl">
              <CheckCircle className="w-5 h-5 shrink-0 mt-0.5 text-teal-600" />
              <div className="text-sm font-medium">{successMessage}</div>
            </div>
          ) : null}

          {edgeError ? (
            <div className="mt-8 border border-red-100 bg-red-50 px-5 py-4 flex items-start gap-3 text-red-800 rounded-2xl">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-red-500" />
              <div className="text-sm font-medium">{edgeError}</div>
            </div>
          ) : null}

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 bg-white border border-slate-100 p-8 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 tracking-tight leading-none">Current Plan</h2>
                  <p className="mt-2 text-sm font-medium text-teal-600">
                    {planName} {hasActiveSubscription ? 'Membership' : 'Account'}
                  </p>
                </div>

                {hasActiveSubscription ? (
                  <span className={`inline-flex items-center px-4 py-1.5 border rounded-full text-xs font-semibold tracking-wide ${cancelAtPeriodEnd
                    ? 'bg-amber-50 border-amber-200 text-amber-700'
                    : 'bg-teal-50 border-teal-100 text-teal-700'
                    }`}>
                    {cancelAtPeriodEnd ? 'Cancellation Scheduled' : 'Active'}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-4 py-1.5 border border-slate-200 bg-slate-50 text-slate-500 rounded-full text-xs font-semibold tracking-wide">
                    No active subscription
                  </span>
                )}
              </div>

              {loading ? (
                <div className="mt-10 flex items-center justify-center py-10">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-teal-500 border-t-transparent" />
                </div>
              ) : hasActiveSubscription ? (
                <div className="mt-10 space-y-8">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    <div className="border border-slate-100 bg-slate-50 rounded-2xl p-6 transition-colors shadow-sm">
                      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Price</div>
                      <div className="mt-4 text-3xl font-bold text-slate-900 tracking-tight">
                        {discountedPriceText || basePriceText || '—'}
                        <span className="ml-1 text-sm font-medium text-slate-500">{intervalSuffix}</span>
                      </div>
                      {discountedPriceText && basePriceText ? (
                        <div className="mt-2 text-xs font-medium text-slate-400 line-through">{basePriceText}{intervalSuffix}</div>
                      ) : null}
                    </div>

                    <div className="border border-slate-100 bg-slate-50 rounded-2xl p-6 transition-colors shadow-sm">
                      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Next Billing Date</div>
                      <div className="mt-4 text-xl font-bold text-slate-900 tracking-tight">
                        {nextBillDate ? formatDate(nextBillDate) : '—'}
                      </div>
                      {cancelAtPeriodEnd && cancelDate ? (
                        <div className="mt-2 text-xs font-medium text-amber-600">Access until {cancelDate}</div>
                      ) : (
                        <div className="mt-2 text-xs font-medium text-slate-500">Cancel anytime</div>
                      )}
                    </div>

                    <div className="border border-slate-100 bg-slate-50 rounded-2xl p-6 transition-colors shadow-sm">
                      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</div>
                      <div className="mt-4 text-2xl font-bold text-slate-900 tracking-tight capitalize">
                        {subscriptionStatus ? subscriptionStatus : '—'}
                      </div>
                      {discountSummary?.percentOff ? (
                        <div className="mt-2 text-xs font-medium text-teal-600">{discountSummary.percentOff}% off next payment</div>
                      ) : downsellRedeemedAt ? (
                        <div className="mt-2 text-xs font-medium text-teal-600">Offer applied</div>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-slate-100 mt-8">
                    <button
                      type="button"
                      onClick={handleUpdatePaymentMethod}
                      disabled={activeAction === 'payment'}
                      className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-teal-700 text-white font-semibold hover:bg-teal-800 transition-all shadow-sm hover:shadow-md rounded-full disabled:opacity-70 flex-1"
                    >
                      <CreditCard className="w-5 h-5 text-teal-100" />
                      {activeAction === 'payment' ? 'Opening...' : 'Update Payment'}
                    </button>

                    <button
                      type="button"
                      onClick={handleRequestCancel}
                      disabled={cancelAtPeriodEnd || activeAction === 'cancel'}
                      className={`inline-flex items-center justify-center gap-2 px-8 py-3.5 font-semibold transition-all rounded-full flex-1 border ${cancelAtPeriodEnd
                        ? 'bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed'
                        : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm'
                        }`}
                    >
                      {activeAction === 'cancel' ? 'Processing...' : cancelAtPeriodEnd ? 'Scheduled' : 'Cancel'}
                    </button>
                  </div>

                  {cancelAtPeriodEnd && cancelDate ? (
                    <div className="text-sm font-medium text-slate-600 bg-amber-50 border border-amber-100 rounded-2xl p-4 mt-6">
                      Cancellation scheduled. You will retain access until <span className="font-bold text-slate-800">{cancelDate}</span>.
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="mt-12 text-center py-12 border border-slate-100 bg-slate-50 rounded-2xl">
                  <p className="text-slate-500 font-medium text-sm">No active subscription found.</p>
                  <button
                    type="button"
                    onClick={() => navigate('/membership')}
                    className="mt-6 inline-flex items-center justify-center px-8 py-3.5 bg-teal-700 text-white font-semibold hover:bg-teal-800 transition-all shadow-sm hover:shadow-md rounded-full"
                  >
                    View Upgrade Options
                  </button>
                </div>
              )}
            </div>

            <div className="bg-white border border-slate-100 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-3xl h-fit">
              <h2 className="text-xl font-bold text-slate-900 tracking-tight border-b border-slate-100 pb-4">Account Data</h2>
              <div className="mt-6 space-y-6">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</div>
                  <div className="mt-2 text-sm text-slate-700 break-all bg-slate-50 p-3 rounded-xl border border-slate-100">{localPreviewMode ? 'Not shared' : (user?.email || '—')}</div>
                </div>

                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Join Date</div>
                  <div className="mt-2 text-sm font-medium text-slate-800">
                    {profile?.subscription_start_date ? formatDate(profile.subscription_start_date) : '—'}
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Plan Level</div>
                  <div className="mt-2 text-sm font-medium text-slate-800 capitalize">{planName}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showDownsellOffer ? (
        <ModalShell title="Wait... Before You Go." onClose={() => setShowDownsellOffer(false)}>
          <p className="text-sm text-slate-600 font-medium leading-relaxed mb-8">
            Keep your current plan and securely receive <span className="font-bold text-teal-600">50% off your next renewal</span>.
            This is a one-time offer.
          </p>

          <div className="mt-8 space-y-3 flex flex-col">
            <button
              type="button"
              onClick={applyDownsell}
              disabled={activeAction === 'downsell'}
              className="w-full flex justify-center py-3.5 px-6 bg-teal-600 text-white font-semibold rounded-full hover:bg-teal-700 transition-all shadow-sm hover:shadow-md disabled:opacity-70"
            >
              {activeAction === 'downsell' ? 'Applying...' : 'Claim 50% Off Offer'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowDownsellOffer(false);
                setShowCancelConfirm(true);
              }}
              disabled={activeAction === 'downsell'}
              className="w-full flex justify-center py-3.5 px-6 bg-white border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 transition-all rounded-full disabled:opacity-70 shadow-sm"
            >
              Continue Cancellation
            </button>
          </div>
        </ModalShell>
      ) : null}

      {showCancelConfirm ? (
        <ModalShell title="Confirm Cancellation" tone="danger" onClose={() => setShowCancelConfirm(false)}>
          <p className="text-sm text-slate-600 font-medium leading-relaxed mb-8">
            You will retain access to your plan until the end of your current billing cycle.
          </p>
          <div className="mt-8 space-y-3 flex flex-col">
            <button
              type="button"
              onClick={confirmCancel}
              disabled={activeAction === 'cancel'}
              className="w-full flex justify-center py-3.5 px-6 bg-red-600 text-white font-semibold rounded-full hover:bg-red-700 transition-all shadow-sm hover:shadow-md disabled:opacity-70"
            >
              {activeAction === 'cancel' ? 'Scheduling...' : 'Confirm Cancellation'}
            </button>
            <button
              type="button"
              onClick={() => setShowCancelConfirm(false)}
              disabled={activeAction === 'cancel'}
              className="w-full flex justify-center py-3.5 px-6 bg-white border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 transition-all rounded-full disabled:opacity-70 shadow-sm"
            >
              Go Back
            </button>
          </div>
        </ModalShell>
      ) : null}
    </>
  );
};

export default ManageSubscription;
